// src/components/provider/ProviderReviews.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getProviderReviews, addProviderReview } from '../../services/enhancedFirestore';
import StarRating from '../common/StarRating';

const ProviderReviews = ({ providerId, providerName }) => {
  const { currentUser, isParent } = useAuth();
  
  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [userReview, setUserReview] = useState(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewData, setReviewData] = useState({
    rating: 0,
    comment: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Fetch reviews on component mount
  useEffect(() => {
    const fetchReviews = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const fetchedReviews = await getProviderReviews(providerId);
        setReviews(fetchedReviews);
        
        // Calculate average rating
        if (fetchedReviews.length > 0) {
          const total = fetchedReviews.reduce((sum, review) => sum + review.rating, 0);
          setAverageRating(total / fetchedReviews.length);
        }
        
        // Check if current user has already left a review
        if (currentUser) {
          const existingReview = fetchedReviews.find(
            review => review.parentId === currentUser.uid
          );
          
          if (existingReview) {
            setUserReview(existingReview);
            setReviewData({
              rating: existingReview.rating,
              comment: existingReview.comment
            });
          }
        }
      } catch (err) {
        console.error('Error fetching reviews:', err);
        setError('Failed to load reviews. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchReviews();
  }, [providerId, currentUser]);
  
  // Handle rating change
  const handleRatingChange = (newRating) => {
    setReviewData(prev => ({
      ...prev,
      rating: newRating
    }));
  };
  
  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setReviewData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle review submission
  const handleSubmitReview = async (e) => {
    e.preventDefault();
    
    if (!currentUser) {
      setError('You must be logged in to leave a review');
      return;
    }
    
    if (!isParent()) {
      setError('Only parents can leave reviews for providers');
      return;
    }
    
    if (reviewData.rating === 0) {
      setError('Please select a rating');
      return;
    }
    
    setSubmitLoading(true);
    setError(null);
    setSuccessMessage('');
    
    try {
      const result = await addProviderReview(providerId, currentUser.uid, reviewData);
      
      if (userReview) {
        // Update existing review in the list
        setReviews(prevReviews => 
          prevReviews.map(review => 
            review.id === userReview.id 
              ? { ...review, ...reviewData, updatedAt: new Date() } 
              : review
          )
        );
        
        setSuccessMessage('Your review has been updated!');
      } else {
        // Add new review to the list
        setReviews(prevReviews => [
          { 
            id: result.id, 
            providerId, 
            parentId: currentUser.uid, 
            ...reviewData, 
            createdAt: new Date() 
          },
          ...prevReviews
        ]);
        
        setUserReview({ 
          id: result.id, 
          providerId, 
          parentId: currentUser.uid, 
          ...reviewData 
        });
        
        setSuccessMessage('Your review has been submitted!');
      }
      
      // Recalculate average rating
      const newTotal = reviews.reduce((sum, review) => {
        // Skip the user's previous review in the calculation
        if (userReview && review.id === userReview.id) {
          return sum;
        }
        return sum + review.rating;
      }, reviewData.rating);
      
      const newCount = userReview ? reviews.length : reviews.length + 1;
      setAverageRating(newTotal / newCount);
      
      // Hide form after successful submission
      setShowReviewForm(false);
    } catch (err) {
      console.error('Error submitting review:', err);
      setError('Failed to submit review. Please try again later.');
    } finally {
      setSubmitLoading(false);
    }
  };
  
  // Format date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp instanceof Date 
      ? timestamp 
      : new Date(timestamp.seconds ? timestamp.seconds * 1000 : timestamp);
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  return (
    <div className="provider-reviews">
      <div className="reviews-header">
        <h2>Provider Reviews</h2>
        <div className="rating-summary">
          <div className="average-rating">
            <StarRating value={averageRating} readonly size="large" />
            <span className="rating-value">{averageRating.toFixed(1)}</span>
            <span className="review-count">({reviews.length} reviews)</span>
          </div>
          
          {currentUser && isParent() && (
            <button 
              className="write-review-button"
              onClick={() => setShowReviewForm(!showReviewForm)}
            >
              {userReview ? 'Edit Your Review' : 'Write a Review'}
            </button>
          )}
        </div>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}
      
      {/* Review Form */}
      {showReviewForm && (
        <div className="review-form-container">
          <h3>{userReview ? 'Edit Your Review' : `Review ${providerName}`}</h3>
          
          <form onSubmit={handleSubmitReview} className="review-form">
            <div className="form-group">
              <label>Your Rating:</label>
              <StarRating 
                value={reviewData.rating} 
                onChange={handleRatingChange} 
                size="medium"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="comment">Your Review:</label>
              <textarea
                id="comment"
                name="comment"
                value={reviewData.comment}
                onChange={handleInputChange}
                placeholder="Share your experience with this provider..."
                rows={5}
              />
            </div>
            
            <div className="form-actions">
              <button 
                type="button" 
                className="cancel-button"
                onClick={() => setShowReviewForm(false)}
                disabled={submitLoading}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="submit-button"
                disabled={submitLoading}
              >
                {submitLoading 
                  ? 'Submitting...' 
                  : (userReview ? 'Update Review' : 'Submit Review')}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Reviews List */}
      <div className="reviews-list">
        {loading ? (
          <p>Loading reviews...</p>
        ) : reviews.length === 0 ? (
          <p className="no-reviews">No reviews yet. Be the first to review this provider!</p>
        ) : (
          reviews.map(review => (
            <div 
              key={review.id} 
              className={`review-card ${review.parentId === currentUser?.uid ? 'user-review' : ''}`}
            >
              <div className="review-header">
                <StarRating value={review.rating} readonly size="small" />
                <span className="review-date">{formatDate(review.createdAt)}</span>
              </div>
              
              <div className="review-content">
                <p>{review.comment}</p>
              </div>
              
              {review.parentId === currentUser?.uid && (
                <div className="review-actions">
                  <button 
                    className="edit-button"
                    onClick={() => setShowReviewForm(true)}
                  >
                    Edit Review
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ProviderReviews;